import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  username: string | null; // For backward compatibility
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Derive username from user email for backward compatibility
  const username = user?.email?.split('@')[0] || null;

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      // Set username in localStorage if session exists
      if (session?.user?.email) {
        try {
          const username = session.user.email.split('@')[0];
          localStorage.setItem('bidflow_username', username);
        } catch { /* quota exceeded */ }
      }
      
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event);
        setSession(session);
        setUser(session?.user ?? null);
        
        // Set or clear username in localStorage based on session
        if (session?.user?.email) {
          try {
            const username = session.user.email.split('@')[0];
            localStorage.setItem('bidflow_username', username);
          } catch { /* quota exceeded */ }
        } else {
          localStorage.removeItem('bidflow_username');
        }
        
        setIsLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Login error:', error);
        return { success: false, error: error.message };
      }

      // Save username to localStorage for project fetching
      if (data.user?.email) {
        try {
          const username = data.user.email.split('@')[0];
          localStorage.setItem('bidflow_username', username);
          console.log('✅ Username saved to localStorage:', username);
        } catch { /* quota exceeded */ }
      }

      return { success: true };
    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: 'An unexpected error occurred' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    
    // Clear username from localStorage
    localStorage.removeItem('bidflow_username');
    console.log('🚪 Username cleared from localStorage');
  };

  return (
    <AuthContext.Provider value={{ user, session, username, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
